import { motion } from "framer-motion";
import Layout from "../common/Layout";
import { usePageMeta } from "../common/usePageMeta";
import { track } from "../features/analytics/tracker";

const SUBSCRIBE_URL = "https://subscribe.teardrophydro.co.uk";

const PERKS = [
  {
    icon: "🌿",
    title: "Always First Pick",
    body: "Subscribers get priority on the harvest. The freshest cuts, reserved before we open to walk-ins.",
  },
  {
    icon: "📅",
    title: "You Set the Cadence",
    body: "Weekly, fortnightly, or monthly, you choose. Pause or skip any time, no questions asked.",
  },
  {
    icon: "✏️",
    title: "Tell Us Exactly What You Want",
    body: "Specific crops and quantities, or just your preferences and we'll pack the best of today's harvest.",
  },
  {
    icon: "💸",
    title: "No Surprise Charges",
    body: "We confirm every order with you before charging. You agree the price, then we deliver.",
  },
];

const STEPS = [
  { n: "1", title: "Create a free account",    body: "Sign up at subscribe.teardrophydro.co.uk in under a minute." },
  { n: "2", title: "Tell us what you want",    body: "Pick crops, quantities, and frequency, or just set preferences and leave the rest to us." },
  { n: "3", title: "We confirm & price",       body: "We'll review your order and reply with a price. No payment until you've agreed." },
  { n: "4", title: "Fresh to your door",       body: "Deliveries roll automatically. Edit, pause, or cancel any time from your dashboard." },
];

function SubscribeCTA({ label = "Set Up a Subscription", style }) {
  return (
    <a
      href={SUBSCRIBE_URL}
      className="btn btn-primary"
      style={{ minWidth: 240, minHeight: 52, fontSize: 17, ...style }}
      onClick={() => track("subscribe_page_cta_click", { label })}
    >
      {label}
    </a>
  );
}

export default function SubscribePage() {
  usePageMeta({ title: 'Weekly Fresh Greens Subscription | TearDrop Hydro', description: 'Set up a weekly box of just-harvested herbs, salads and microgreens, delivered across the Hockley area within hours of cutting.', path: '/subscribe' });
  return (
    <Layout>
      {/* HERO */}
      <section
        style={{
          position: "relative",
          overflow: "hidden",
          minHeight: "clamp(380px, 55svh, 620px)",
          display: "flex",
          alignItems: "center",
        }}
      >
        <img
          src="/images/products/mesclunMix/IMG_6780.JPEG"
          alt=""
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 40%" }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(135deg, rgba(10,28,20,.82) 0%, rgba(10,28,20,.52) 60%, transparent 100%)",
          }}
        />
        <div className="container" style={{ position: "relative", zIndex: 1, padding: "var(--s-10) var(--s-5)" }}>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="eyebrow" style={{ color: "var(--accent)", marginBottom: "var(--s-3)" }}>
              Recurring deliveries
            </p>
            <h1
              className="h1"
              style={{
                color: "#fff",
                maxWidth: 620,
                marginTop: 0,
                marginBottom: "var(--s-4)",
                fontSize: "clamp(28px, 5vw, 48px)",
                lineHeight: 1.1,
              }}
            >
              Fresh greens on your doorstep,
              <br />
              <em>every single week.</em>
            </h1>
            <p
              style={{
                color: "rgba(255,255,255,.82)",
                fontSize: "clamp(16px, 2vw, 18px)",
                maxWidth: 500,
                marginBottom: "var(--s-8)",
                lineHeight: 1.65,
              }}
            >
              Set up a recurring delivery of exactly what you want, harvested the same morning, delivered within 2 hours.
            </p>
            <div style={{ display: "flex", gap: "var(--s-4)", flexWrap: "wrap" }}>
              <a
                href={SUBSCRIBE_URL}
                className="btn btn-primary"
                style={{
                  background: "var(--accent)",
                  color: "var(--brand)",
                  fontWeight: 700,
                  padding: "var(--s-4) var(--s-7)",
                  fontSize: 17,
                }}
                onClick={() => track("subscribe_page_hero_cta_click")}
              >
                Get Started Free
              </a>
              <a
                href={`${SUBSCRIBE_URL}/how-it-works`}
                className="btn"
                style={{
                  background: "transparent",
                  color: "#fff",
                  border: "1.5px solid rgba(255,255,255,.4)",
                  padding: "var(--s-4) var(--s-7)",
                  fontSize: 16,
                  borderRadius: "var(--r-md)",
                }}
                onClick={() => track("subscribe_page_hiw_click")}
              >
                How It Works
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PERKS GRID */}
      <section className="section">
        <div className="container">
          <div style={{ textAlign: "center", marginBottom: "var(--s-8)" }}>
            <p className="eyebrow">Why subscribe</p>
            <h2 className="h2">More than a box scheme</h2>
            <p className="lead u-maxw-28r" style={{ margin: "0 auto" }}>
              No middleman, no cold chain. Just us, growing it and bringing it straight to you on a schedule that works.
            </p>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "var(--s-5)",
            }}
          >
            {PERKS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: i * 0.07 }}
                style={{
                  background: "var(--panel)",
                  borderRadius: "var(--r-xl)",
                  padding: "var(--s-7) var(--s-6)",
                  boxShadow: "var(--shadow-card)",
                  border: "1px solid var(--border)",
                }}
              >
                <div style={{ fontSize: 32, marginBottom: "var(--s-4)" }}>{p.icon}</div>
                <h3 className="h3" style={{ marginBottom: "var(--s-3)" }}>{p.title}</h3>
                <p className="muted" style={{ margin: 0, fontSize: "var(--fs-14)", lineHeight: 1.6 }}>{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section alt">
        <div className="container" style={{ maxWidth: 700 }}>
          <p className="eyebrow">Simple process</p>
          <h2 className="h2">Up and running in minutes</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--s-6)", marginTop: "var(--s-6)" }}>
            {STEPS.map(({ n, title, body }) => (
              <motion.div
                key={n}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.38 }}
                style={{ display: "flex", gap: "var(--s-5)", alignItems: "flex-start" }}
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: "var(--r-md)",
                    background: "var(--brand)",
                    color: "var(--accent)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "var(--fs-18)",
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {n}
                </div>
                <div>
                  <h4 className="h4" style={{ marginBottom: "var(--s-2)" }}>{title}</h4>
                  <p className="muted" style={{ margin: 0, fontSize: "var(--fs-14)", lineHeight: 1.6 }}>{body}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <div style={{ marginTop: "var(--s-9)" }}>
            <SubscribeCTA label="Create Your Account Free" />
          </div>
        </div>
      </section>

      {/* PHOTO STRIP */}
      <section style={{ overflow: "hidden", padding: "var(--s-8) 0", background: "var(--panel-2)" }}>
        <motion.div
          style={{ display: "flex", gap: "var(--s-4)", paddingInline: "var(--s-5)" }}
          initial={{ x: 0 }}
          animate={{ x: "-50%" }}
          transition={{ repeat: Infinity, duration: 30, ease: "linear" }}
        >
          {[
            "/images/products/lettuce/IMG_4868.JPEG",
            "/images/products/genoBasil/IMG_5987.JPEG",
            "/images/products/mesclunMix/IMG_6779.JPEG",
            "/images/products/coriander/IMG_6755.JPEG",
            "/images/products/mint/IMG_6827.JPEG",
            "/images/products/sorrel/IMG_5044.JPEG",
          ].flatMap((src, i) => [
            <div
              key={`a${i}`}
              style={{
                width: 200,
                height: 140,
                borderRadius: "var(--r-md)",
                overflow: "hidden",
                flexShrink: 0,
                boxShadow: "var(--shadow-card)",
              }}
            >
              <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>,
            <div
              key={`b${i}`}
              style={{
                width: 200,
                height: 140,
                borderRadius: "var(--r-md)",
                overflow: "hidden",
                flexShrink: 0,
                boxShadow: "var(--shadow-card)",
              }}
            >
              <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>,
          ])}
        </motion.div>
      </section>

      {/* BOTTOM CTA */}
      <section className="section">
        <div className="container" style={{ textAlign: "center", maxWidth: 560 }}>
          <p className="eyebrow">Ready?</p>
          <h2 className="h2" style={{ marginBottom: "var(--s-4)" }}>
            Get fresh greens on subscription today
          </h2>
          <p className="lead" style={{ marginBottom: "var(--s-7)" }}>
            No commitment, no card needed to start. Create an account, tell us what you want, and we'll be in touch.
          </p>
          <SubscribeCTA label="Start Your Subscription" style={{ display: "inline-flex" }} />
          <p className="muted" style={{ fontSize: 13, marginTop: "var(--s-3)" }}>
            Or browse today's one-off products at{" "}
            <a href="https://teardrophydro.co.uk/browse-products" style={{ color: "var(--brand-2)", textDecoration: "underline" }}>
              teardrophydro.co.uk
            </a>
          </p>
        </div>
      </section>
    </Layout>
  );
}
